import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Snackbar,
  styled,
  Typography,
} from "~/common/mui";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { downloadFile } from "~/common/utils/ApiHelper";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import type { InvoiceInfoData } from "~/domains/orders/types/models";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";

const FileDownloadPopup = ({
  invoiceData,
  onCloseDownload,
}: {
  invoiceData: InvoiceInfoData;
  onCloseDownload: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={true} maxWidth="tablet">
      <DialogHeader onClose={onCloseDownload}>
        <DialogTitle>{t("orderDetailsPage.summary.download.title")}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        {invoiceData.invoiceNo && invoiceData.invoiceUrl && (
          <DowloadGrid>
            <Typography>
              {t("orderList.downloadItem.invoice.label", {
                invoiceNo: invoiceData.invoiceNo,
              })}
            </Typography>
            <CopyToClipboard
              text={invoiceData.invoiceNo ?? ""}
              snackbarMessage={t("orderList.downloadItem.invoice.clipboardText")}
            />
            {invoiceData.invoiceNo && invoiceData.invoiceUrl && (
              <TooltipWithArrow title={t("orderDetails.download.text")} placement={"top"}>
                <IconButton
                  onClick={() => downloadFile(`${invoiceData?.invoiceUrl}?id=${invoiceData?.invoiceNo}&type=INVOICE`)}
                >
                  <FileDownloadOutlinedIcon color="primary" />
                </IconButton>
              </TooltipWithArrow>
            )}
          </DowloadGrid>
        )}
        {invoiceData.deliveryNoteNumber && invoiceData?.deliveryNoteUrl && (
          <DowloadGrid>
            <Typography>
              {t("orderList.downloadItem.deliveryNote.label", {
                deliveryNoteNumber: invoiceData.deliveryNoteNumber,
              })}
            </Typography>
            <CopyToClipboard
              text={invoiceData.deliveryNoteNumber ?? ""}
              snackbarMessage={t("orderList.downloadItem.deliveryNote.clipboardText")}
            />
            {invoiceData.deliveryNoteNumber && invoiceData.deliveryNoteUrl && (
              <TooltipWithArrow title={t("orderDetails.download.text")} placement={"top"}>
                <IconButton
                  onClick={() =>
                    downloadFile(`${invoiceData?.deliveryNoteUrl}?id=${invoiceData?.deliveryNoteNumber}&type=DELNOTE`)
                  }
                >
                  <FileDownloadOutlinedIcon color="primary" />
                </IconButton>
              </TooltipWithArrow>
            )}
          </DowloadGrid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseDownload} variant="text">
          {t("cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileDownloadPopup;

export const DowloadGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: "8px",
  gridTemplateColumns: "1fr 40px 40px",
}));

const CopyToClipboard = ({ text, snackbarMessage }: { text: string; snackbarMessage?: string }) => {
  const { t } = useTranslation();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const copyClipboard = async () => {
    await navigator.clipboard.writeText(text);
    setOpenSnackbar(true);
  };

  return (
    <>
      <TooltipWithArrow title={t("generic.copyToClipboard")} placement={"top"}>
        <IconButton onClick={copyClipboard}>
          <ContentCopyIcon color="primary" />
        </IconButton>
      </TooltipWithArrow>

      <Snackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        autoHideDuration={3000}
        transitionDuration={{ exit: 500, appear: 500, enter: 500 }}
        message={snackbarMessage ?? t("generic.copiedToClipboard")}
        action={<SnackbarCloseButton onClick={() => setOpenSnackbar(false)} />}
      />
    </>
  );
};
